import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { FaCog, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { get, put } from '../../utils/makeRequest';

const ApiSettingsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [apiProvider, setApiProvider] = useState('auto');
  const [cdfBasePair, setCdfBasePair] = useState('USD/INR4');
  const [usdinrSessionMarkup, setUsdinrSessionMarkup] = useState('0.0175');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchApiSettings();
  }, []);

  const fetchApiSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await get('/admin/api-settings');
      if (response) {
        if (response.apiProvider) {
          setApiProvider(response.apiProvider);
        }
        if (response.cdfBasePair) {
          setCdfBasePair(response.cdfBasePair);
        } else if (
          response.apiProvider === 'currencydatafeed' &&
          response.basePair
        ) {
          setCdfBasePair(response.basePair);
        }
        if (
          response.usdinrSessionMarkup !== undefined &&
          response.usdinrSessionMarkup !== null
        ) {
          setUsdinrSessionMarkup(String(response.usdinrSessionMarkup));
        }
      }
    } catch (error) {
      console.error('Error fetching API settings:', error);
      setError('Failed to load API settings');
      // Default to auto if error
      setApiProvider('auto');
      setCdfBasePair('USD/INR4');
      setUsdinrSessionMarkup('0.0175');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      
      // Validate base pair format (allows XXX/YYY or XXX/YYY4 format)
      if (apiProvider === 'currencydatafeed' && cdfBasePair && !cdfBasePair.match(/^[A-Z]{3}\/[A-Z]{3}\d*$/)) {
        setError('Base pair must be in format XXX/YYY or XXX/YYY4 (e.g., USD/INR or USD/INR4)');
        toast.error('Invalid base pair format');
        setSaving(false);
        return;
      }

      const markupNumber = Number(usdinrSessionMarkup);
      if (!Number.isFinite(markupNumber) || markupNumber < 0 || markupNumber > 10) {
        setError('USDINR session markup must be a number between 0 and 10 (e.g. 0.0175)');
        toast.error('Invalid USDINR session markup');
        setSaving(false);
        return;
      }
      
      await put('/admin/api-settings', { 
        apiProvider,
        basePair: apiProvider === 'currencydatafeed' ? cdfBasePair : undefined,
        usdinrSessionMarkup: markupNumber,
      });
      setSuccess(true);
      toast.success('API Settings saved successfully!');
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving API settings:', error);
      setError(error.response?.data?.error || 'Failed to save API settings');
      toast.error('Failed to save API settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row>
        <Col>
          <Card>
            <Card.Header className="bg-primary text-white">
              <h4 className="mb-0">
                <FaCog className="me-2" />
                API Settings
              </h4>
            </Card.Header>
            <Card.Body>
              {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                  <FaExclamationTriangle className="me-2" />
                  {error}
                </Alert>
              )}
              {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess(false)}>
                  <FaCheckCircle className="me-2" />
                  API Settings saved successfully!
                </Alert>
              )}
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <strong>Forex Data Provider</strong>
                  </Form.Label>
                  <Form.Select
                    value={apiProvider}
                    onChange={(e) => setApiProvider(e.target.value)}
                    disabled={saving}
                  >
                    <option value="auto">Auto (Finage + Currency Data Feed + 1Forge)</option>
                    <option value="finage">Finage</option>
                    <option value="currencydatafeed">Currency Data Feed</option>
                    <option value="1forge">1Forge</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Select the API provider for fetching forex snapshot data. This will affect all snapshot API endpoints.
                  </Form.Text>
                </Form.Group>
                
                {apiProvider === 'currencydatafeed' && (
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <strong>Base Currency Pair</strong>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="USD/INR or USD/INR4"
                      value={cdfBasePair}
                      onChange={(e) => setCdfBasePair(e.target.value.toUpperCase())}
                      disabled={saving}
                    />
                    <Form.Text className="text-muted">
                      Enter the base currency pair for Currency Data Feed API (e.g., USD/INR or USD/INR4). All other INR pairs will be derived from this base pair.
                    </Form.Text>
                  </Form.Group>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>
                    <strong>USDINR Session Markup</strong>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    step="0.0001"
                    min="0"
                    max="10"
                    value={usdinrSessionMarkup}
                    onChange={(e) => setUsdinrSessionMarkup(e.target.value)}
                    disabled={saving}
                  />
                  <Form.Text className="text-muted">
                    Added to USDINR mid Mon–Fri 09:00–15:30 IST before Redis write (default 0.0175). Set 0 to disable.
                  </Form.Text>
                </Form.Group>
                
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Saving...
                    </>
                  ) : (
                    'Save Settings'
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ApiSettingsTab;
